import dotenv from 'dotenv';
import fs from 'fs/promises';
import os from 'os';
import path from 'path';

import type { AppServer, ModelSchema } from '../types';
import socketioServer from '@/websocket/socketio/server';
import { initRoles } from '../auth/role';
import sessionModule from '../auth/session';
import { RoleDefinition } from '../auth/types';
import userModule from '../auth/user';
import { getLocalConfigs } from '../config/local';
import { loadConfigs, setSchema } from '../config/server';
import { startConfigSync, loadRemoteConfigs } from '../config/sync';
import { ConfigSchema } from '../config/types';
import cronModule, { defineCronJob, getCronJobsMetadata, startCronJobs } from '../cron/jobs';
import { type IndexReconcileMode, Store } from '../data/store';
import { resolveStores } from '../data/resolveStores';
import { connect, getClient, getMongodbUri } from '../db/client';
import { _createSystemMutation, _createSystemQuery, createMutation, createQuery } from '../methods';
import {
  MigrationScript,
  default as migrationModule,
  runMigrations,
  startMigrations,
} from '../migration';
import rateLimitModule from '../rate-limit';
import { initRateLimits } from '../rate-limit/rules';
import systemModule from '../system';
import lockModule, { acquireLock, releaseLock } from '../lock';
import filesModule from '../files';
import { viteServer } from '../viteServer';
import { connectCloudBackend } from './backendApi';
import { initMetrics } from './metrics';
import { Module } from './module';
import { startServer } from './server';
import { markAppStarted, setMetadata } from './state';
import { time } from '@/time';
import { EmailConfig, setEmailConfig } from './emailConfig';
import { managedEmailProvider } from './managedEmailProvider';
import { AuthConfig, setAuthConfig } from './authConfig';
import { SecurityConfig, setSecurityConfig } from './securityConfig';
import { WebsocketConfig, setWebsocketConfig } from './websocketConfig';
import { buildAuthRateLimits } from '../auth/user';

export type AppOptions = {
  modules?: Module[];
  server?: AppServer;
  email?: EmailConfig;
  auth?: AuthConfig;
  /** Security settings such as clickjacking protection. See {@link SecurityConfig}. */
  security?: SecurityConfig;
  /**
   * Custom role definitions keyed by role name. Defined roles are synced to the
   * Modelence Cloud dashboard for user management. See {@link RoleDefinition}.
   *
   * @example
   * ```typescript
   * startApp({
   *   roles: {
   *     admin: { description: 'Full access to all features' },
   *     editor: { description: 'Can edit content' },
   *     viewer: {},
   *   },
   * });
   * ```
   */
  roles?: Record<string, RoleDefinition>;
  /** @internal */
  defaultRoles?: Record<string, string>;
  migrations?: Array<MigrationScript>;
  /** WebSocket settings, used by live queries and channels. See {@link WebsocketConfig}. */
  websocket?: WebsocketConfig;
  /** Enable server-side rendering of the user's React tree. */
  ssr?: boolean;
};

export async function startApp({
  modules = [],
  roles = {},
  defaultRoles = {},
  server = viteServer,
  migrations = [],
  email = {},
  auth = {},
  security = {},
  websocket = {},
  ssr = false,
}: AppOptions) {
  if (ssr && server === viteServer) {
    viteServer.enableSsr();
  }
  dotenv.config();

  dotenv.config({ path: '.modelence.env' });

  const hasRemoteBackend = Boolean(process.env.MODELENCE_SERVICE_ENDPOINT);

  trackAppStart()
    .then(() => {
      // Do nothing
    })
    .catch(() => {
      // Silently ignore tracking errors to not disrupt app startup
    });

  for (const module of modules) {
    if (module.name.toLowerCase().startsWith('_system.')) {
      throw new Error(
        `Invalid module name: '${module.name}'\n\n` +
          `The '_system.' prefix is reserved for internal use and cannot be used in user-defined modules.\n\n` +
          `Rename your module to something that does not start with '_system.'`
      );
    }
  }
  const systemModules = [
    userModule,
    sessionModule,
    cronModule,
    migrationModule,
    rateLimitModule,
    systemModule,
    lockModule,
    filesModule,
  ];
  const combinedModules = [...systemModules, ...modules];

  markAppStarted();

  initSystemMethods(systemModules);
  initCustomMethods(modules);

  initRoles(roles, defaultRoles);

  const configSchema = getConfigSchema(combinedModules);
  setSchema(configSchema);
  const rawStores = getStores(combinedModules) as Store<ModelSchema, never>[];
  const channels = getChannels(combinedModules);

  defineCronJobs(combinedModules);

  // Apply user-provided auth rate limit overrides directly onto the user
  // module so its `rateLimits` array reflects the effective configuration
  // (the Modelence Cloud reads these from modules for display).
  userModule.rateLimits = buildAuthRateLimits(auth.rateLimits);

  const rateLimits = getRateLimits(combinedModules);
  initRateLimits(rateLimits);

  const { storesToInit, effectiveStores } = resolveStores(rawStores) as {
    storesToInit: Store<ModelSchema, never>[];
    effectiveStores: Store<ModelSchema, never>[];
  };

  if (hasRemoteBackend) {
    const { configs, environmentId, appAlias, environmentAlias, telemetry } =
      await connectCloudBackend({
        configSchema,
        cronJobsMetadata: getCronJobsMetadata(),
        stores: effectiveStores,
        roles,
      });
    loadRemoteConfigs(configs);
    setMetadata({ environmentId, appAlias, environmentAlias, telemetry });
  } else {
    loadConfigs(getLocalConfigs(configSchema));
  }

  // Default to Modelence's managed email provider when the app is connected
  // to Modelence Cloud and no provider was configured. This relays through
  // Studio's /api/email/send (which sends via SES on a Modelence-owned
  // domain). Locally — without a service endpoint — keep the historical
  // "no provider configured" behavior so misconfigurations still throw loudly.
  if (hasRemoteBackend && !email.provider) {
    setEmailConfig({ ...email, provider: managedEmailProvider });
  } else {
    setEmailConfig(email);
  }
  setAuthConfig(auth);
  setSecurityConfig(security);
  const websocketsEnabled = websocket.enabled !== false;
  setWebsocketConfig({
    ...websocket,
    provider: websocketsEnabled ? websocket.provider || socketioServer : undefined,
  });
  if (!websocketsEnabled && channels.length > 0) {
    const channelNames = channels.map((channel) => channel.category).join(', ');
    console.warn(
      `Channels are defined (${channelNames}) but websocket.enabled is false, ` +
        'so their broadcasts will not be delivered.'
    );
  }

  const mongodbUri = getMongodbUri();
  if (mongodbUri) {
    await connect();
    const allStoresToInit = [...new Set([...storesToInit, ...effectiveStores])];
    initStores(allStoresToInit);
    await createIndexesAndMigrationsWithLock(effectiveStores, migrations);
  } else {
    startMigrations(migrations);
  }

  if (hasRemoteBackend) {
    await initMetrics();
    startConfigSync();
  }

  startCronJobs().catch(console.error);

  await startServer(server, { combinedModules, channels });
}

function initCustomMethods(modules: Module[]) {
  for (const module of modules) {
    for (const [key, handler] of Object.entries(module.queries)) {
      createQuery(`${module.name}.${key}`, handler);
    }
    for (const [key, handler] of Object.entries(module.mutations)) {
      createMutation(`${module.name}.${key}`, handler);
    }
  }
}

function initSystemMethods(modules: Module[]) {
  for (const module of modules) {
    for (const [key, handler] of Object.entries(module.queries)) {
      _createSystemQuery(`${module.name}.${key}`, handler);
    }
    for (const [key, handler] of Object.entries(module.mutations)) {
      _createSystemMutation(`${module.name}.${key}`, handler);
    }
  }
}

function getStores(modules: Module[]) {
  return modules.flatMap((module) => module.stores);
}

function getChannels(modules: Module[]) {
  return modules.flatMap((module) => module.channels);
}

function getRateLimits(modules: Module[]) {
  return modules.flatMap((module) => module.rateLimits);
}

function warnIndexCreationFailure(storeName: string, error: unknown) {
  console.warn(`Failed to create indexes for store '${storeName}'. Continuing startup.`, error);
}

const MIGRATIONS_LOCK_RESOURCE = 'migrations';

async function createIndexesAndMigrationsWithLock(
  effectiveStores: Store<ModelSchema, never>[],
  migrations: MigrationScript[]
) {
  const hasLock = await acquireLock(MIGRATIONS_LOCK_RESOURCE, {
    lockDuration: time.seconds(30),
    heartbeat: true,
  });
  if (!hasLock) {
    return;
  }

  let blockingStores: Store<ModelSchema, never>[];
  let backgroundStores: Store<ModelSchema, never>[];

  try {
    blockingStores = effectiveStores.filter((store) => store.getIndexCreationMode() === 'blocking');
    backgroundStores = effectiveStores.filter(
      (store) => store.getIndexCreationMode() === 'background'
    );

    for (const store of blockingStores) {
      await createStoreIndexes(store, 'full');
    }
    for (const store of backgroundStores) {
      await createStoreIndexes(store, 'drop-only');
    }
  } catch (error) {
    await releaseLock(MIGRATIONS_LOCK_RESOURCE);
    throw error;
  }

  const backgroundIndexCreationPromise = (async () => {
    for (const store of backgroundStores) {
      await createStoreIndexes(store, 'create-only');
    }
  })();
  const migrationPromise = runMigrations(migrations, { lockMode: 'skip' });

  void Promise.allSettled([backgroundIndexCreationPromise, migrationPromise])
    .then(([backgroundIndexesResult, migrationResult]) => {
      if (backgroundIndexesResult.status === 'rejected') {
        console.error('Error creating background indexes:', backgroundIndexesResult.reason);
      }

      if (migrationResult.status === 'rejected') {
        console.error('Error running migrations:', migrationResult.reason);
      }
    })
    .finally(async () => {
      await releaseLock(MIGRATIONS_LOCK_RESOURCE);
    });
}

async function createStoreIndexes(
  store: Store<ModelSchema, never>,
  reconcileMode: IndexReconcileMode = 'full'
) {
  const storeName = store.getName();

  try {
    await store.createIndexes(reconcileMode);
  } catch (error) {
    warnIndexCreationFailure(storeName, error);
  }
}

function getConfigSchema(modules: Module[]): ConfigSchema {
  const merged: ConfigSchema = {};

  for (const module of modules) {
    for (const [key, value] of Object.entries(module.configSchema)) {
      const absoluteKey = `${module.name}.${key}`;
      if (absoluteKey in merged) {
        throw new Error(`Duplicate config schema key: ${absoluteKey} (${module.name})`);
      }

      merged[absoluteKey] = value;
    }
  }

  return merged;
}

function defineCronJobs(modules: Module[]) {
  for (const module of modules) {
    for (const [cronAlias, cronJobParams] of Object.entries(module.cronJobs)) {
      defineCronJob(`${module.name}.${cronAlias}`, cronJobParams);
    }
  }
}

function initStores(stores: Store<ModelSchema, never>[]) {
  const client = getClient();
  if (!client) {
    throw new Error('Failed to initialize stores: MongoDB client not initialized');
  }

  for (const store of stores) {
    store.init(client);
  }
}

async function trackAppStart() {
  const isTrackingEnabled = process.env.MODELENCE_TRACKING_ENABLED !== 'false';

  if (isTrackingEnabled) {
    const serviceEndpoint = process.env.MODELENCE_SERVICE_ENDPOINT ?? 'https://cloud.modelence.com';
    const environmentId = process.env.MODELENCE_ENVIRONMENT_ID;

    const appDetails = await getAppDetails();
    const modelencePackageJson = await import('../../package.json');

    await fetch(`${serviceEndpoint}/api/track/app-start`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        projectName: appDetails.name,
        version: modelencePackageJson.default.version,
        localHostname: os.hostname(),
        environmentId,
      }),
    });
  }
}

async function getAppDetails() {
  try {
    const packageJsonPath = path.join(process.cwd(), 'package.json');
    const packageJsonContent = await fs.readFile(packageJsonPath, 'utf-8');
    const packageJson = JSON.parse(packageJsonContent);

    return {
      name: packageJson.name || 'unknown',
    };
  } catch {
    return {
      name: 'unknown',
    };
  }
}
